import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, ArrowLeft, Save, Trash2, Upload, X, Bold, Italic, Heading2, Heading3, Link2, Plus, ImagePlus, List, ListOrdered, Minus, Table as TableIcon, Quote, Unlink, Youtube } from 'lucide-react';
import { rawFetch } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import ImageResize from 'tiptap-extension-resize-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Typography } from '@tiptap/extension-typography';
import YoutubeExtension from '@tiptap/extension-youtube';
import { NodeSelection } from '@tiptap/pm/state';
import TableBubbleMenu from '../components/TableBubbleMenu';
import { buildTenantPath } from '../hooks/useTenant';

interface Membership {
  id: number;
  name: string;
}

interface TransmissionData {
  id?: number;
  headline: string;
  content: string | null;
  memberships: number[];
  sentAsEmail: boolean;
  bannerImage: string | null;
}

export default function TransmissionEditorPage({ slug, transmissionId }: { slug: string; transmissionId: string }) {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [transmission, setTransmission] = useState<TransmissionData>({
    headline: '',
    content: null,
    memberships: [],
    sentAsEmail: false,
    bannerImage: null,
  });
  const transmissionRef = useRef(transmission);
  transmissionRef.current = transmission;

  // Banner upload state
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Auto-save debounce
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transmissionIdRef = useRef(transmissionId);

  // Link input state for bubble menu
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  // YouTube embed modal state
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const youtubeInputRef = useRef<HTMLInputElement>(null);

  const isNew = transmissionId === 'new';

  // Image upload helper
  const uploadContentImage = useCallback(async (file: File): Promise<string | null> => {
    try {
      const optimized = await optimizeImage(file);

      const presignRes = await rawFetch(`/api/public/tenants/transmissions/content-image/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contentType: 'image/webp', fileSize: optimized.size }),
      });

      if (!presignRes.ok) throw new Error('Presign failed');
      const presignJson = await presignRes.json() as { data: { uploadUrl: string; path: string } };

      const uploadRes = await fetch(presignJson.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/webp' },
        body: optimized,
      });

      if (!uploadRes.ok) throw new Error('Upload failed');

      const confirmRes = await rawFetch(`/api/public/tenants/transmissions/content-image/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path: presignJson.data.path }),
      });

      if (!confirmRes.ok) throw new Error('Confirm failed');
      const confirmJson = await confirmRes.json() as { data: { url: string } };
      return confirmJson.data.url;
    } catch (err) {
      console.error('Content image upload failed:', err);
      return null;
    }
  }, [slug, token]);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        blockquote: {},
        horizontalRule: {},
        bulletList: {},
        orderedList: {},
        link: false,
      }),
      Placeholder.configure({
        placeholder: 'Initiate your transmission here...',
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: 'text-electric-cyan underline underline-offset-2 hover:text-electric-cyan/80 transition-colors cursor-pointer',
        },
      }),
      ImageResize.configure({
        inline: true,
        minWidth: 100,
        maxWidth: 800,
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-white/10 w-full',
        },
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'border border-white/10',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-white/10 p-2 text-gray-300',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-white/10 p-2 bg-white/5 text-white font-semibold',
        },
      }),
      Typography,
      YoutubeExtension.configure({
        nocookie: true,
        controls: true,
        allowFullscreen: true,
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'rounded-lg overflow-hidden my-4',
        },
      }),
    ],
    content: null,
    onUpdate: ({ editor }) => {
      // Trigger auto-save on content change
      scheduleAutoSave(editor.getJSON());
    },
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[300px] prose prose-invert max-w-none prose-headings:text-white prose-p:text-gray-300 prose-a:text-electric-cyan prose-strong:text-white prose-img:rounded-lg prose-img:max-w-full prose-blockquote:border-electric-cyan/40 prose-blockquote:text-gray-400 prose-hr:border-white/10',
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;

        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (imageFiles.length === 0) return false;

        event.preventDefault();
        imageFiles.forEach(async (file) => {
          const url = await uploadContentImage(file);
          if (url && editor) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        });
        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        const imageItem = Array.from(items).find(item => item.type.startsWith('image/'));
        if (!imageItem) return false;

        const file = imageItem.getAsFile();
        if (!file) return false;

        event.preventDefault();
        (async () => {
          const url = await uploadContentImage(file);
          if (url && editor) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        })();
        return true;
      },
      handleKeyDown: (view, event) => {
        // Delete entire table when Backspace/Delete is pressed and table is empty
        if ((event.key === 'Backspace' || event.key === 'Delete') && editor?.isActive('table')) {
          const { state } = view;
          const { selection } = state;
          // Check if it's a NodeSelection on the table
          if (selection instanceof NodeSelection && selection.node.type.name === 'table') {
            editor.chain().focus().deleteTable().run();
            return true;
          }
          // Check if all cells in the table are empty
          const $pos = state.doc.resolve(selection.from);
          for (let depth = $pos.depth; depth > 0; depth--) {
            const node = $pos.node(depth);
            if (node.type.name === 'table') {
              let hasContent = false;
              node.descendants((child) => {
                if (child.isText && child.text && child.text.trim().length > 0) {
                  hasContent = true;
                }
              });
              if (!hasContent) {
                editor.chain().focus().deleteTable().run();
                return true;
              }
              break;
            }
          }
        }
        return false;
      },
    },
  });

  // Auto-save logic (2s debounce)
  const scheduleAutoSave = useCallback((json: any) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(async () => {
      // Only auto-save if we have an existing transmission (not brand new without headline)
      const currentId = transmissionIdRef.current;
      if (currentId === 'new') return; // Don't auto-save unsaved new transmissions

      setAutoSaveStatus('saving');
      try {
        const payload = {
          content: JSON.stringify(json),
          headline: transmissionRef.current.headline,
          memberships: transmissionRef.current.memberships,
          sentAsEmail: transmissionRef.current.sentAsEmail,
          bannerImage: transmissionRef.current.bannerImage,
        };

        await rawFetch(`/api/public/tenants/${slug}/transmissions/${currentId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      } catch {
        setAutoSaveStatus('idle');
      }
    }, 2000);
  }, [slug, token]);

  // File input ref for image upload via floating menu
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    e.target.value = '';

    const url = await uploadContentImage(file);
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor, uploadContentImage]);

  // Link helpers for bubble menu
  const openLinkInput = useCallback(() => {
    if (!editor) return;
    const existingUrl = editor.getAttributes('link').href || '';
    setLinkUrl(existingUrl);
    setShowLinkInput(true);
    setTimeout(() => linkInputRef.current?.focus(), 50);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor]);

  // YouTube embed helper
  const openYoutubeModal = useCallback(() => {
    setYoutubeUrl('');
    setShowYoutubeModal(true);
    setTimeout(() => youtubeInputRef.current?.focus(), 50);
  }, []);

  const embedYoutube = useCallback(() => {
    if (!editor || !youtubeUrl.trim()) return;
    editor.commands.setYoutubeVideo({ src: youtubeUrl.trim() });
    setShowYoutubeModal(false);
    setYoutubeUrl('');
  }, [editor, youtubeUrl]);

  useEffect(() => {
    const init = async () => {
      await fetchMemberships();
      if (!isNew) {
        await fetchTransmission();
      } else {
        setLoading(false);
      }
    };
    init();

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const fetchMemberships = async () => {
    try {
      const res = await rawFetch(`/api/public/tenants/${slug}/memberships`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json() as { data: Membership[] };
        setMemberships(json.data);
      }
    } catch {}
  };

  const fetchTransmission = async () => {
    try {
      const res = await rawFetch(`/api/public/tenants/${slug}/transmissions/${transmissionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json() as { data: any };
        const d = json.data;
        let membershipIds: number[] = [];
        if (d.memberships) {
          try { membershipIds = JSON.parse(d.memberships); } catch {}
        }
        setTransmission({
          id: d.id,
          headline: d.headline,
          content: d.content,
          memberships: membershipIds,
          sentAsEmail: d.sentAsEmail === 1,
          bannerImage: d.bannerImage,
        });
        if (d.bannerImage) {
          setBannerUrl(d.bannerImage);
        }
        // Load content into editor
        if (d.content && editor) {
          try {
            const parsed = JSON.parse(d.content);
            editor.commands.setContent(parsed);
          } catch {}
        }
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch transmission:', err);
      setLoading(false);
    }
  };

  // Set editor content when editor becomes ready and transmission is loaded
  useEffect(() => {
    if (editor && transmission.content && !isNew) {
      try {
        const parsed = JSON.parse(transmission.content);
        editor.commands.setContent(parsed);
      } catch {}
    }
  }, [editor, transmission.content]);

  const handleBannerUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) return;

    setUploadingBanner(true);
    setUploadProgress(0);

    try {
      let currentId = transmissionId;
      if (isNew) {
        const createRes = await rawFetch(`/api/public/tenants/${slug}/transmissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ headline: transmission.headline || 'Untitled Transmission', memberships: transmission.memberships, sentAsEmail: transmission.sentAsEmail }),
        });
        if (createRes.ok) {
          const createJson = await createRes.json() as { data: any };
          currentId = String(createJson.data.id);
          transmissionIdRef.current = currentId;
          window.history.replaceState(null, '', buildTenantPath(slug, `transmissions/${currentId}`));
        } else {
          throw new Error('Failed to create transmission');
        }
      }

      const optimizedFile = await optimizeImage(file);

      const presignRes = await rawFetch(`/api/public/tenants/transmissions/${currentId}/banner/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contentType: 'image/webp', fileSize: optimizedFile.size }),
      });

      if (!presignRes.ok) throw new Error('Failed to get upload URL');
      const presignJson = await presignRes.json() as { data: { uploadUrl: string; path: string } };

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignJson.data.uploadUrl, true);
        xhr.setRequestHeader('Content-Type', 'image/webp');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 90));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(95);
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(optimizedFile);
      });

      const confirmRes = await rawFetch(`/api/public/tenants/transmissions/${currentId}/banner/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path: presignJson.data.path }),
      });

      if (confirmRes.ok) {
        const confirmJson = await confirmRes.json() as { data: { bannerUrl: string; bannerImage: string } };
        setBannerUrl(confirmJson.data.bannerUrl);
        setTransmission(prev => ({ ...prev, bannerImage: confirmJson.data.bannerImage }));
        setUploadProgress(100);
      }
    } catch (err) {
      console.error('Banner upload failed:', err);
    } finally {
      setTimeout(() => {
        setUploadingBanner(false);
        setUploadProgress(0);
      }, 1000);
    }
  };

  const optimizeImage = async (file: File): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          resolve(blob || file);
        }, 'image/webp', 0.82);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const removeBanner = () => {
    setBannerUrl(null);
    setTransmission(prev => ({ ...prev, bannerImage: null }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let contentJson: string | null = null;
      if (editor) {
        contentJson = JSON.stringify(editor.getJSON());
      }

      const payload = {
        headline: transmission.headline,
        content: contentJson,
        memberships: transmission.memberships,
        sentAsEmail: transmission.sentAsEmail,
        bannerImage: transmission.bannerImage,
        status: 'draft',
      };

      let res;
      if (isNew) {
        res = await rawFetch(`/api/public/tenants/${slug}/transmissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      } else {
        res = await rawFetch(`/api/public/tenants/${slug}/transmissions/${transmissionId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        const json = await res.json() as { data: any };
        if (isNew && json.data?.id) {
          window.location.href = buildTenantPath(slug, `transmissions/${json.data.id}`);
        }
      }
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this transmission? This cannot be undone.')) return;
    try {
      await rawFetch(`/api/public/tenants/${slug}/transmissions/${transmissionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = buildTenantPath(slug, 'transmissions');
    } catch {}
  };

  const toggleMembership = (id: number) => {
    setTransmission(prev => ({
      ...prev,
      memberships: prev.memberships.includes(id)
        ? prev.memberships.filter(m => m !== id)
        : [...prev.memberships, id],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      {/* Top navigation bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a
              href={buildTenantPath(slug, 'transmissions')}
              className="flex items-center gap-1 text-text-muted hover:text-electric-cyan transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs font-mono uppercase tracking-wider">Back</span>
            </a>
            {/* Auto-save indicator */}
            {autoSaveStatus === 'saving' && (
              <span className="text-xs text-text-muted font-mono flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Saving...
              </span>
            )}
            {autoSaveStatus === 'saved' && (
              <span className="text-xs text-electric-cyan/70 font-mono">Saved</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isNew && (
              <button
                onClick={handleDelete}
                className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 font-mono uppercase tracking-wider transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !transmission.headline.trim()}
              className="btn-primary text-sm !px-4 !py-1.5 inline-flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-24 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Headline */}
        <input
          type="text"
          value={transmission.headline}
          onChange={(e) => setTransmission(prev => ({ ...prev, headline: e.target.value }))}
          placeholder="Transmission headline..."
          className="w-full text-3xl font-bold bg-transparent border-none outline-none text-white placeholder:text-white/20 mb-6"
        />

        {/* Memberships */}
        <div className="mb-8">
          <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Target Memberships</label>
          <div className="flex flex-wrap gap-2">
            {memberships.map(m => (
              <button
                key={m.id}
                onClick={() => toggleMembership(m.id)}
                className={`px-3 py-1.5 rounded text-xs font-mono border transition-colors ${
                  transmission.memberships.includes(m.id)
                    ? 'bg-electric-cyan/20 border-electric-cyan/50 text-electric-cyan'
                    : 'bg-surface border-white/10 text-text-secondary hover:border-white/30'
                }`}
              >
                {m.name}
              </button>
            ))}
            {memberships.length === 0 && (
              <span className="text-xs text-text-muted">No memberships configured</span>
            )}
          </div>
        </div>

        {/* Banner Image Upload */}
        <div className="mb-8">
          <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Banner Image</label>
          {bannerUrl ? (
            <div className="relative group">
              <div className="w-full h-48 rounded-lg overflow-hidden border border-white/10">
                <img
                  src={bannerUrl}
                  alt="Banner"
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              <button
                onClick={removeBanner}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-red-500/80 text-white transition-colors opacity-0 group-hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-electric-cyan/30 transition-colors bg-surface/50">
              {uploadingBanner ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
                  <div className="w-48">
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-electric-cyan rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-text-secondary text-center mt-1">{uploadProgress}%</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-text-muted mb-2" />
                  <span className="text-sm text-text-secondary">Click to upload banner image</span>
                  <span className="text-xs text-text-muted mt-1">JPEG, PNG or WebP</span>
                </>
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleBannerUpload(file);
                  e.target.value = '';
                }}
                disabled={uploadingBanner}
              />
            </label>
          )}
        </div>

        {/* Send as Email Toggle */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary">Send as Email</label>
              <p className="text-xs text-text-muted mt-0.5">Broadcast this transmission via email to selected memberships</p>
            </div>
            <button
              type="button"
              onClick={() => setTransmission(prev => ({ ...prev, sentAsEmail: !prev.sentAsEmail }))}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-electric-cyan/30 ${
                transmission.sentAsEmail ? 'bg-electric-cyan' : 'bg-white/10'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  transmission.sentAsEmail ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* TipTap Content Editor */}
        <div className="mb-8">
          <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Content</label>
          <div className="glass-card p-4 relative">
            {/* Bubble Menu - appears over selected text */}
            {editor && (
              <BubbleMenu
                editor={editor}
                pluginKey="textFormatMenu"
                shouldShow={({ editor, state }) => {
                  const { from, to } = state.selection;
                  // Hide when no text is selected or when inside a table
                  if (from === to) return false;
                  if (editor.isActive('table')) return false;
                  return true;
                }}
                options={{ placement: 'top', offset: 8 }}
                className="flex items-center gap-0.5 bg-[#1A1A1A] border border-white/10 rounded-lg p-1 shadow-xl"
              >
                {showLinkInput ? (
                  <div className="flex items-center gap-1 px-1">
                    <input
                      ref={linkInputRef}
                      type="url"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyLink();
                        }
                        if (e.key === 'Escape') {
                          setShowLinkInput(false);
                          setLinkUrl('');
                        }
                      }}
                      placeholder="https://..."
                      className="w-48 px-2 py-1 text-xs bg-white/5 border border-white/10 rounded text-white placeholder:text-white/30 outline-none focus:border-electric-cyan/50"
                    />
                    <button
                      onClick={applyLink}
                      className="p-1.5 rounded text-electric-cyan hover:bg-electric-cyan/10 transition-colors"
                      title="Apply link"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                    </button>
                    {editor.isActive('link') && (
                      <button
                        onClick={removeLink}
                        className="p-1.5 rounded text-red-400 hover:bg-red-400/10 transition-colors"
                        title="Remove link"
                      >
                        <Unlink className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => { setShowLinkInput(false); setLinkUrl(''); }}
                      className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                      title="Cancel"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      className={`p-1.5 rounded transition-colors ${editor.isActive('bold') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                      title="Bold"
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      className={`p-1.5 rounded transition-colors ${editor.isActive('italic') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                      title="Italic"
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <div className="w-px h-4 bg-white/10 mx-0.5" />
                    <button
                      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                      className={`p-1.5 rounded transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                      title="Heading 2"
                    >
                      <Heading2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                      className={`p-1.5 rounded transition-colors ${editor.isActive('heading', { level: 3 }) ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                      title="Heading 3"
                    >
                      <Heading3 className="w-4 h-4" />
                    </button>
                    <div className="w-px h-4 bg-white/10 mx-0.5" />
                    <button
                      onClick={openLinkInput}
                      className={`p-1.5 rounded transition-colors ${editor.isActive('link') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                      title="Link"
                    >
                      <Link2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleBlockquote().run()}
                      className={`p-1.5 rounded transition-colors ${editor.isActive('blockquote') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                      title="Blockquote"
                    >
                      <Quote className="w-4 h-4" />
                    </button>
                  </>
                )}
              </BubbleMenu>
            )}

            {/* Floating Menu - appears on empty lines */}
            {editor && (
              <FloatingMenu
                editor={editor}
                options={{ placement: 'top', offset: 8 }}
                className="flex items-center gap-1"
              >
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
                  title="Heading 2"
                >
                  <Heading2 className="w-3.5 h-3.5" />
                  <span>H2</span>
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
                  title="Bullet List"
                >
                  <List className="w-3.5 h-3.5" />
                  <span>List</span>
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
                  title="Ordered List"
                >
                  <ListOrdered className="w-3.5 h-3.5" />
                  <span>1. 2. 3.</span>
                </button>
                <button
                  onClick={() => editor.chain().focus().setHorizontalRule().run()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
                  title="Horizontal Rule"
                >
                  <Minus className="w-3.5 h-3.5" />
                  <span>Divider</span>
                </button>
                <button
                  onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
                  title="Insert Table (3×3)"
                >
                  <TableIcon className="w-3.5 h-3.5" />
                  <span>Table</span>
                </button>
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
                  title="Upload image"
                >
                  <ImagePlus className="w-3.5 h-3.5" />
                  <span>Image</span>
                </button>
                <button
                  onClick={openYoutubeModal}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
                  title="Embed YouTube video"
                >
                  <Youtube className="w-3.5 h-3.5" />
                  <span>YouTube</span>
                </button>
              </FloatingMenu>
            )}

            <EditorContent editor={editor} />

            {/* Table Bubble Menu - appears when cursor is in a table */}
            {editor && <TableBubbleMenu editor={editor} />}

            {/* Hidden file input for image uploads */}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleImageSelect}
            />
          </div>
        </div>

        {/* YouTube Embed Modal */}
        {showYoutubeModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 w-full max-w-md shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  <Youtube className="w-5 h-5 text-red-500" />
                  Embed YouTube Video
                </h3>
                <button
                  onClick={() => { setShowYoutubeModal(false); setYoutubeUrl(''); }}
                  className="p-1 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <input
                ref={youtubeInputRef}
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    embedYoutube();
                  }
                  if (e.key === 'Escape') {
                    setShowYoutubeModal(false);
                    setYoutubeUrl('');
                  }
                }}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 outline-none focus:border-electric-cyan/50 transition-colors"
              />
              <p className="text-xs text-text-muted mt-2">Paste any YouTube or YouTube Shorts URL</p>
              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  onClick={() => { setShowYoutubeModal(false); setYoutubeUrl(''); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-mono text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={embedYoutube}
                  disabled={!youtubeUrl.trim()}
                  className="px-4 py-1.5 rounded-lg text-xs font-mono bg-electric-cyan text-black font-semibold hover:bg-electric-cyan/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Embed
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
